/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Mail, Home } from "lucide-react";
import { billetterieApi } from "@/lib/api";
import { useSeo } from "@/lib/seo";

export default function BilletterieConfirmation() {
  useSeo({
    title: "Confirmation de commande | My Animation",
    description: "Confirmation de votre achat de billets My Animation.",
    path: "/billetterie/confirmation",
    noindex: true,
  });
  const { slug } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", attempts: 0, order: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", order: null });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await billetterieApi.checkoutStatus(sessionId);
        if (cancelled) return;
        if (res.payment_status === "paid") {
          setState({ status: "paid", order: res.order });
          // NOTE : GA4 `purchase` reporté à une V2 avec envoi serveur (Measurement Protocol)
          // pour éviter tout risque de perte d'événement (fermeture page, consent refusé,
          // gtag non chargé, erreur JS). Aucun flag `analytics_sent` marqué côté serveur.
          return;
        }
        if (res.status === "expired" || res.payment_status === "failed") {
          setState({ status: "failed", order: res.order });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 12) {
          setState({ status: "timeout", order: res.order });
          return;
        }
        setTimeout(poll, 2500);
      } catch (e) {
        if (!cancelled) setState({ status: "error", order: null });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6" data-testid="billetterie-confirmation">
      <div className="max-w-xl w-full text-center">
        {state.status === "polling" && (
          <>
            <Loader2 size={48} className="animate-spin text-[#DCB457] mx-auto mb-6" />
            <h2 className="font-serif-luxe text-3xl">Confirmation du paiement…</h2>
            <p className="text-white/55 mt-3 text-sm">Merci de patienter, cela peut prendre quelques secondes.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <div className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #34D399, #10B981)" }}>
              <CheckCircle2 size={48} strokeWidth={1.5} className="text-white" />
            </div>
            <p className="text-[#dcb457] text-xs uppercase tracking-[0.25em] mb-2">Paiement validé</p>
            <h1 className="font-serif-luxe text-4xl md:text-5xl">Merci pour votre achat !</h1>
            <p className="text-white/75 mt-6 leading-relaxed">
              Vos billets ont été envoyés à <strong className="text-white">{state.order?.buyer_email}</strong>.
              <br />Vérifiez votre boîte mail (et le dossier indésirable si besoin).
            </p>
            <div className="mt-8 p-5 bg-white/5 border border-white/10 text-left text-sm space-y-1.5">
              <p className="text-white/55 text-xs uppercase tracking-[0.18em] mb-2">Récapitulatif</p>
              <p>Commande <span className="font-mono text-[#DCB457]">#{state.order?.id?.slice(0, 8).toUpperCase()}</span></p>
              {state.order?.items?.map((it, i) => (
                <p key={i} className="text-white/75">• {it.quantity}× {it.name}</p>
              ))}
              <p className="pt-2 border-t border-white/10">Total payé : <strong className="text-white">{Number(state.order?.total_ttc || 0).toFixed(2)} €</strong></p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to={`/billetterie/${slug}`} className="btn-ghost-gold inline-flex items-center gap-2"><Home size={14} /> Retour à l'événement</Link>
            </div>
          </>
        )}
        {(state.status === "failed" || state.status === "error") && (
          <>
            <XCircle size={48} className="text-red-300 mx-auto mb-6" />
            <h2 className="font-serif-luxe text-3xl">Paiement non finalisé.</h2>
            <p className="text-white/65 mt-3">Le paiement a été annulé ou a échoué. Aucune somme n'a été prélevée.</p>
            <Link to={`/billetterie/${slug}`} className="btn-gold mt-6 inline-flex">Réessayer</Link>
          </>
        )}
        {state.status === "timeout" && (
          <>
            <Mail size={48} className="text-[#DCB457] mx-auto mb-6" />
            <h2 className="font-serif-luxe text-3xl">En cours de traitement.</h2>
            <p className="text-white/65 mt-3">Le paiement met un peu plus de temps que prévu. Vous recevrez vos billets par email dès qu'il est confirmé.</p>
            <Link to={`/billetterie/${slug}`} className="btn-ghost-gold mt-6 inline-flex"><Home size={14} className="mr-2" /> Retour</Link>
          </>
        )}
      </div>
    </div>
  );
}
